<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

interface Props {
  open: boolean
  title: string
  body?: string
  confirmLabel?: string
  tone?: 'default' | 'danger'
}

const props = withDefaults(defineProps<Props>(), { tone: 'default' })
const emit = defineEmits<{ confirm: []; cancel: [] }>()

const confirmButton = ref<HTMLButtonElement | null>(null)

function onKeydown(event: KeyboardEvent) {
  if (props.open && event.key === 'Escape') emit('cancel')
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
watch(
  () => props.open,
  (open) => {
    if (open) requestAnimationFrame(() => confirmButton.value?.focus())
  },
)
</script>

<template>
  <div v-if="props.open" class="dialog-backdrop" @click.self="emit('cancel')">
    <div class="panel dialog stack" role="dialog" aria-modal="true" :aria-label="props.title">
      <h2 class="title-md">{{ props.title }}</h2>
      <p v-if="props.body" class="muted">{{ props.body }}</p>
      <div class="row dialog__actions">
        <button type="button" class="btn btn--ghost" @click="emit('cancel')">
          {{ $t('common.cancel') }}
        </button>
        <button
          ref="confirmButton"
          type="button"
          class="btn"
          :class="props.tone === 'danger' ? 'btn--danger' : 'btn--primary'"
          @click="emit('confirm')"
        >
          {{ props.confirmLabel ?? $t('common.confirm') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dialog-backdrop {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: grid;
  place-items: center;
  padding: var(--space-4);
  background: rgb(0 0 0 / 0.6);
  backdrop-filter: blur(2px);
}

.dialog {
  width: min(28rem, 100%);
}

/* Equal halves: neither choice should look like the safe default by size. */
.dialog__actions .btn {
  flex: 1;
}
</style>
